import React from 'react';
import LocalOpsHelperProfileCard from './LocalOpsHelperProfileCard';
import '../../pages/profile/profile.css';
import './localops.css';

const getMissingLabels = (steps = []) =>
  steps
    .map((step) => step?.label)
    .filter(Boolean)
    .slice(0, 6);

const formatPrice = (price = {}) =>
  price.display ||
  (price.amountCents ? `$${(price.amountCents / 100).toFixed(2)}` : '$9.99');

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export default function LocalOpsHelperSetupGateway({
  accessStatus,
  claimError = '',
  claimingFoundingPass = false,
  error = '',
  loading = false,
  onBack,
  onClaimFoundingPass,
  onOpenProfilePhoto,
  onProfileSaved,
  onRefresh,
  regionSlug = 'camden',
  token,
}) {
  const regionName = accessStatus?.regionDisplayName || 'Camden';
  const access = accessStatus?.access || {};
  const helperProfile = accessStatus?.helperProfile || {};
  const foundingPass = accessStatus?.foundingHelperPass || {};
  const foundingBenefits = accessStatus?.foundingHelperBenefits?.benefits || {};
  const firstLookMinutes = toPositiveNumber(
    foundingBenefits.firstLookTasks?.windowMinutes,
  );
  const feeTier = foundingBenefits.helperFeeTier || {};
  const founderFeePercent = toPositiveNumber(feeTier.founderFeePercent);
  const priceLabel = formatPrice(foundingPass.price);
  const missingCoreLabels = getMissingLabels(helperProfile.missingCoreSteps);
  const missingGuidedLabels = getMissingLabels(helperProfile.missingGuidedSteps);
  const profilePhotoMissing = helperProfile.profilePhotoComplete === false;
  const waitlisted = access.waitlisted === true;
  const temporarilyUnavailable = access.temporarilyUnavailable === true;
  const showClaimFoundingPass =
    !temporarilyUnavailable &&
    foundingPass.checkoutAllowed === true &&
    foundingPass.hasFoundingHelperPass !== true &&
    typeof onClaimFoundingPass === 'function';
  const canShowProfileForm = access.setupAllowed === true && !waitlisted;
  const showWaitlistOffer = waitlisted && showClaimFoundingPass;
  const feeValueText = founderFeePercent
    ? `${founderFeePercent}% platform fee`
    : 'lower platform fee';
  const compactBenefits = [
    firstLookMinutes
      ? `${firstLookMinutes}-minute first-look access on eligible paid tasks`
      : 'First-look access on eligible paid tasks',
    founderFeePercent
      ? `${founderFeePercent}% platform fee`
      : 'Lower platform fee',
    'Founding Helper status',
    'Priority access while helper setup is waitlisted',
  ];

  let eyebrow = 'Helper Setup';
  let title = `Get ready to earn in ${regionName}`;
  let copy =
    'Finish the required helper basics before opening the earn map.';

  if (loading) {
    eyebrow = 'Checking';
    title = 'Checking helper setup';
    copy = 'Loading your LocalOps helper status.';
  } else if (temporarilyUnavailable) {
    eyebrow = 'Temporarily Unavailable';
    title = `${regionName} helper setup is temporarily unavailable`;
    copy = 'Requester tools are still available.';
  } else if (waitlisted) {
    eyebrow = 'Waitlist';
    title = `${regionName} helper setup is currently waitlisted`;
    copy = showWaitlistOffer
      ? "We're limiting new helper setup during launch so the local network stays manageable. Founding Helpers receive priority access."
      : `Founding Helper Pass holders can continue helper setup while ${regionName} is waitlisted.`;
  } else if (access.foundingHelperWaitlistBypass) {
    eyebrow = 'Founding Helper';
    copy = `Your Founding Helper Pass keeps helper setup access open while ${regionName} helper setup is waitlisted.`;
  }

  return (
    <div
      className="localops-helper-setup-gateway"
      data-testid="localops-helper-setup-gateway"
    >
      <div className="localops-helper-setup-gateway__inner">
        <section className="localops-helper-setup-gateway__hero">
          <div>
            <div className="profile-completion-eyebrow">{eyebrow}</div>
            <h1>{title}</h1>
            <p>{error || copy}</p>
            {showWaitlistOffer ? (
              <div className="localops-helper-setup-gateway__waitlist-offer">
                <p>
                  A {regionName} Founding Helper Pass gives you the priority
                  lane during launch, plus first-look task access, Founder
                  status, and the {feeValueText}.
                </p>
                <ul>
                  {compactBenefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
                <small>
                  No guaranteed tasks or earnings. Safety, location, trust,
                  helper setup, and task requirements still apply.
                </small>
              </div>
            ) : null}
          </div>
          <div className="localops-helper-setup-gateway__actions">
            {showClaimFoundingPass ? (
              <button
                className="profile-completion-button"
                disabled={claimingFoundingPass}
                onClick={onClaimFoundingPass}
                type="button"
              >
                {claimingFoundingPass
                  ? 'Opening...'
                  : `Claim Founding Helper Pass - ${priceLabel}`}
              </button>
            ) : null}
            <button
              className="localops-helper-profile-secondary"
              onClick={onRefresh}
              type="button"
            >
              Refresh status
            </button>
            <button
              className="localops-helper-profile-secondary"
              onClick={onBack}
              type="button"
            >
              Back to LocalOps
            </button>
          </div>
        </section>

        {claimError ? (
          <p className="localops-helper-profile-status is-error">{claimError}</p>
        ) : null}

        {missingCoreLabels.length ? (
          <div className="localops-helper-setup-gateway__steps">
            {missingCoreLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        ) : null}

        {profilePhotoMissing ? (
          <button
            className="profile-completion-button localops-helper-setup-gateway__photo"
            onClick={onOpenProfilePhoto}
            type="button"
          >
            Add profile photo
          </button>
        ) : null}

        {canShowProfileForm ? (
          <LocalOpsHelperProfileCard
            foundingHelperRegionSlug={regionSlug}
            onProfileSaved={onProfileSaved}
            token={token}
          />
        ) : null}

        {!canShowProfileForm && missingGuidedLabels.length ? (
          <div className="localops-helper-setup-gateway__guided">
            {missingGuidedLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
